import { useState, useEffect, useCallback } from 'react';
import { useLocalization } from '@/hooks/useLocalization';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Loader2, Layers, TrendingUp, AlertTriangle, CheckCircle2, DollarSign } from 'lucide-react';
import { normalizeHealth } from '@/hooks/useSyncedData';
import { SyncSourceLabel } from '@/components/shared/SyncSourceLabel';
import { ImportReviewStrip } from '@/components/shared/ImportReviewStrip';

interface Project {
  id: string;
  name: string;
  progress: number | null;
  portfolio_id: string | null;
  program_name: string | null;
  budget_total: number | null;
  budget_spent: number | null;
  health_status: string | null;
  start_date: string | null;
  end_date: string | null;
  source: string | null;
  external_id: string | null;
  updated_at: string | null;
  health?: string | null;
  budget?: number | null;
  spent?: number | null;
  category?: string | null;
}

// Connectors populate health/budget/spent (+category); the portfolio-specific
// columns (health_status, budget_total, budget_spent) are only set by manual
// edits and health_status defaults to 'green', so prefer the synced fields.
const HEALTH_TO_RAG = { 'on-track': 'green', 'at-risk': 'amber', critical: 'red' } as const;
function normalizeProject(p: Project): Project {
  return {
    ...p,
    health_status: HEALTH_TO_RAG[normalizeHealth(p.health ?? undefined)],
    budget_total: p.budget_total ?? p.budget ?? null,
    budget_spent: p.budget_spent ?? p.spent ?? null,
  };
}

interface PortfolioGroup {
  id: string;
  label: string;
  projects: Project[];
  totalBudget: number;
  spentBudget: number;
  healthCounts: { green: number; amber: number; red: number };
}

function groupSyncSummary(projects: Project[]): { source: string; syncedAt: string } | null {
  const synced = projects.filter((p) => p.source && p.external_id);
  if (synced.length === 0) return null;
  const sources = Array.from(new Set(synced.map((p) => p.source as string)));
  const syncedAt = synced.reduce<string | null>((latest, p) => {
    if (!p.updated_at) return latest;
    return !latest || p.updated_at > latest ? p.updated_at : latest;
  }, null);
  if (!syncedAt) return null;
  return { source: sources.join(', '), syncedAt };
}

const HEALTH_CONFIG: Record<string, { label: string; className: string; icon: React.ReactNode }> = {
  green: { label: 'On Track',   className: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400', icon: <CheckCircle2 className="h-3 w-3" /> },
  amber: { label: 'At Risk',    className: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',        icon: <AlertTriangle className="h-3 w-3" /> },
  red:   { label: 'Off Track',  className: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',               icon: <AlertTriangle className="h-3 w-3" /> },
};

export const PortfolioView = () => {
  const { tx, t, currency } = useLocalization();
  const fmt = (n: number) => currency(n, { compact: true });
  const { tenantId } = useAuth();
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const query = supabase
      .from('projects_sync')
      .select('id, name, progress, portfolio_id, program_name, budget_total, budget_spent, health_status, health, budget, spent, category, start_date, end_date, source, external_id, updated_at')
      .order('name');
    if (tenantId) query.eq('tenant_id', tenantId);
    const { data, error } = await query;
    if (!error) {
      // Connectors can emit the same project under several sources/ids; keep one per source+name.
      const seen = new Set<string>();
      const rows = ((data ?? []) as unknown as Project[]).filter((p) => {
        const key = `${p.source}:${p.name}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
      setProjects(rows.map(normalizeProject));
    }
    setLoading(false);
  }, [tenantId]);

  useEffect(() => { load(); }, [load]);

  // Group by portfolio name. A portfolio reaches projects_sync three ways: a
  // portfolio id with its name in program_name, the name itself in
  // portfolio_id (document imports), or only a category (connectors). The
  // same name must be one group whichever way it arrived.
  const groups: PortfolioGroup[] = Object.values(
    projects.reduce<Record<string, PortfolioGroup>>((acc, p) => {
      const name = p.portfolio_id ? (p.program_name ?? p.portfolio_id) : p.category;
      const label = name?.trim() || 'Ungrouped Projects';
      const key = name?.trim() ? `name:${label.toLowerCase()}` : '__ungrouped__';

      if (!acc[key]) {
        acc[key] = { id: key, label, projects: [], totalBudget: 0, spentBudget: 0, healthCounts: { green: 0, amber: 0, red: 0 } };
      }
      acc[key].projects.push(p);
      acc[key].totalBudget += p.budget_total ?? 0;
      acc[key].spentBudget += p.budget_spent ?? 0;
      const h = (p.health_status ?? 'green') as 'green' | 'amber' | 'red';
      acc[key].healthCounts[h]++;
      return acc;
    }, {})
  );

  // Summary KPIs
  const totalProjects = projects.length;
  const totalBudget   = projects.reduce((s, p) => s + (p.budget_total ?? 0), 0);
  const totalSpent    = projects.reduce((s, p) => s + (p.budget_spent ?? 0), 0);
  const atRisk        = projects.filter(p => p.health_status === 'amber' || p.health_status === 'red').length;
  const healthTotals  = {
    green: projects.filter(p => (p.health_status ?? 'green') === 'green').length,
    amber: projects.filter(p => p.health_status === 'amber').length,
    red:   projects.filter(p => p.health_status === 'red').length,
  };
  // Completion weighted by budget, over the projects that state both.
  const weighable     = projects.filter(p => (p.budget_total ?? 0) > 0 && p.progress != null);
  const weighableBudget = weighable.reduce((s, p) => s + (p.budget_total ?? 0), 0);
  const completion    = weighableBudget > 0
    ? Math.round((weighable.reduce((s, p) => s + (p.budget_total ?? 0) * (p.progress ?? 0), 0) / weighableBudget) * 10) / 10
    : null;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Layers className="h-6 w-6 text-primary" />
        <div>
          <h2 className="text-2xl font-bold text-foreground">{t('Portfolio Management')}</h2>
          <p className="text-muted-foreground mt-0.5">{t('Aggregate view across all programmes and projects')}</p>
        </div>
      </div>

      <ImportReviewStrip type="project" groupBy="portfolio" onChanged={load} />

      {/* Summary KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <Card>
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wide">{t('Total Projects')}</p>
              <p className="text-3xl font-bold mt-1">{totalProjects}</p>
            </div>
            <Layers className="h-6 w-6 text-muted-foreground" />
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wide">{t('Total Budget')}</p>
              <p className="text-3xl font-bold mt-1">{totalBudget > 0 ? fmt(totalBudget) : '—'}</p>
            </div>
            <DollarSign className="h-6 w-6 text-muted-foreground" />
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wide">{t('Budget Spent')}</p>
              <p className="text-3xl font-bold mt-1">
                {totalBudget > 0 ? `${Math.round((totalSpent / totalBudget) * 100)}%` : '—'}
              </p>
              {totalSpent > 0 && <p className="text-[10px] text-muted-foreground">{fmt(totalSpent)}</p>}
            </div>
            <TrendingUp className="h-6 w-6 text-muted-foreground" />
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wide">{t('Completion')}</p>
              <p className="text-3xl font-bold mt-1">{completion !== null ? `${completion}%` : '—'}</p>
              <p className="text-[10px] text-muted-foreground">{t('budget-weighted')}</p>
            </div>
            <CheckCircle2 className="h-6 w-6 text-muted-foreground" />
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wide">{t('At Risk')}</p>
              <p className="text-3xl font-bold mt-1 text-amber-500">{atRisk}</p>
              {totalProjects > 0 && (
                <p className="text-[10px] text-muted-foreground">
                  {healthTotals.green} {t('on track')} · {healthTotals.amber} {t('at risk')} · {healthTotals.red} {t('off track')}
                </p>
              )}
            </div>
            <AlertTriangle className="h-6 w-6 text-amber-500" />
          </CardContent>
        </Card>
      </div>

      {/* Portfolio groups */}
      {projects.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16 text-muted-foreground">
            <Layers className="h-10 w-10 mb-4 opacity-30" />
            <p className="text-sm">{t('No projects yet. Projects synced from integrations will appear here.')}</p>
          </CardContent>
        </Card>
      ) : (
        groups.map((group) => {
          const syncSummary = groupSyncSummary(group.projects);
          return (
          <Card key={group.id}>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-base">{tx(group.label)}</CardTitle>
                  <CardDescription>{group.projects.length}  {t('project')}{group.projects.length !== 1 ? 's' : ''}</CardDescription>
                </div>
                <div className="flex items-center gap-3 text-xs text-muted-foreground">
                  {group.totalBudget > 0 && (
                    <span>{fmt(group.spentBudget)} / {fmt(group.totalBudget)}</span>
                  )}
                  <div className="flex gap-1.5">
                    {group.healthCounts.green > 0 && <Badge className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 text-[10px] px-1.5">{group.healthCounts.green}  {t('on track')}</Badge>}
                    {group.healthCounts.amber > 0 && <Badge className="bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 text-[10px] px-1.5">{group.healthCounts.amber}  {t('at risk')}</Badge>}
                    {group.healthCounts.red   > 0 && <Badge className="bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 text-[10px] px-1.5">{group.healthCounts.red}  {t('off track')}</Badge>}
                  </div>
                </div>
              </div>
              {group.totalBudget > 0 && (
                <Progress value={(group.spentBudget / group.totalBudget) * 100} className="h-1.5 mt-2" />
              )}
            </CardHeader>
            <CardContent className="p-0">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border">
                    <th className="px-6 py-2 text-start text-xs font-medium text-muted-foreground uppercase">{t('Project')}</th>
                    <th className="px-6 py-2 text-start text-xs font-medium text-muted-foreground uppercase">{t('Health')}</th>
                    <th className="px-6 py-2 text-start text-xs font-medium text-muted-foreground uppercase">{t('Progress')}</th>
                    <th className="px-6 py-2 text-end text-xs font-medium text-muted-foreground uppercase">{t('Budget')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {group.projects.map((p) => {
                    const health = HEALTH_CONFIG[p.health_status ?? 'green'];
                    return (
                      <tr key={p.id} className="hover:bg-muted/30 transition-colors">
                        <td className="px-6 py-3 font-medium">{p.name}</td>
                        <td className="px-6 py-3">
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${health.className}`}>
                            {health.icon}{tx(health.label)}
                          </span>
                        </td>
                        <td className="px-6 py-3 text-muted-foreground">{p.progress != null ? `${p.progress}%` : '—'}</td>
                        <td className="px-6 py-3 text-end text-muted-foreground">
                          {p.budget_total ? `${fmt(p.budget_spent ?? 0)} / ${fmt(p.budget_total)}` : '—'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </CardContent>
            {syncSummary && (
              <div className="px-6 pb-4">
                <SyncSourceLabel source={syncSummary.source} syncedAt={syncSummary.syncedAt} />
              </div>
            )}
          </Card>
          );
        })
      )}
    </div>
  );
};

export default PortfolioView;
